import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import { listSubscriptionsPage, getPhonePeStatus, revokeSubscription, getSubscriptionStateCounts } from "../api";
import SubscriptionDetailsModal from "../components/SubscriptionDetailsModal";
import DateRangeFilter, { presetRange } from "../components/DateRangeFilter";

const PAGE_SIZE = 50;

const STATES = ["", "INITIATED", "ACTIVATION_IN_PROGRESS", "INTEROP_IN_PROGRESS", "ACTIVE", "PAUSED", "PAUSE_IN_PROGRESS", "CANCELLED", "CANCEL_IN_PROGRESS", "REVOKE_IN_PROGRESS", "REVOKED", "FAILED"];

const STATE_STYLE = {
  ACTIVE:                    { bg: "#dcfce7", color: "#166534" },
  INITIATED:                 { bg: "#dbeafe", color: "#1e40af" },
  ACTIVATION_IN_PROGRESS:    { bg: "#dbeafe", color: "#1e40af" },
  INTEROP_IN_PROGRESS:       { bg: "#dbeafe", color: "#1e40af" },
  PAUSED:                    { bg: "#fef9c3", color: "#854d0e" },
  PAUSE_IN_PROGRESS:         { bg: "#fef9c3", color: "#854d0e" },
  CANCELLED:                 { bg: "#f1f5f9", color: "#475569" },
  CANCEL_IN_PROGRESS:        { bg: "#f1f5f9", color: "#475569" },
  REVOKE_IN_PROGRESS:        { bg: "#f1f5f9", color: "#475569" },
  REVOKED:                   { bg: "#f1f5f9", color: "#475569" },
  FAILED:                    { bg: "#fee2e2", color: "#991b1b" },
};

function Badge({ state }) {
  const style = STATE_STYLE[state] || { bg: "#f1f5f9", color: "#475569" };
  return (
    <span style={{ ...t.badge, backgroundColor: style.bg, color: style.color }}>
      {state}
    </span>
  );
}

function fmt(epochMs) {
  if (!epochMs) return "—";
  return new Date(epochMs).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

// created_at is a tz-aware timestamp from the API, unlike the epoch-ms date columns
// above. Same shape as fmtDateTime in AppTransactions.js so the two tables agree.
function fmtDateTime(val) {
  if (!val) return "—";
  return new Date(val).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function fmtAmount(paise) {
  return `₹${(paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
}

function PhoneStatusModal({ appId, apiKey, phoneNumber, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getPhonePeStatus(appId, apiKey, phoneNumber)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [appId, apiKey, phoneNumber]);

  const STATUS_COLOR = {
    ACTIVE: { bg: "#dcfce7", color: "#166534" },
    NOT_FOUND: { bg: "#f1f5f9", color: "#475569" },
    INITIATED: { bg: "#dbeafe", color: "#1e40af" },
    ACTIVATION_IN_PROGRESS: { bg: "#dbeafe", color: "#1e40af" },
    PAUSED: { bg: "#fef9c3", color: "#854d0e" },
    CANCELLED: { bg: "#f1f5f9", color: "#475569" },
    REVOKED: { bg: "#f1f5f9", color: "#475569" },
    FAILED: { bg: "#fee2e2", color: "#991b1b" },
  };

  const stateStyle = data ? (STATUS_COLOR[data.state] || { bg: "#f1f5f9", color: "#475569" }) : {};

  return (
    <div style={m.overlay} onClick={onClose}>
      <div style={{ ...m.box, maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <div style={m.header}>
          <h2 style={m.title}>Status — {phoneNumber}</h2>
          <button onClick={onClose} style={m.closeBtn}>✕</button>
        </div>
        {loading && <div style={s.loading}>Fetching live status…</div>}
        {error && <div style={{ color: "#991b1b", fontSize: 13 }}>{error}</div>}
        {data && (
          <div style={m.grid}>
            <div style={m.field}>
              <span style={m.label}>State</span>
              <span style={{ ...t.badge, backgroundColor: stateStyle.bg, color: stateStyle.color, alignSelf: "flex-start" }}>{data.state}</span>
            </div>
            <div style={m.field}><span style={m.label}>Subscription ID</span><code style={m.value}>{data.subscription_id || "—"}</code></div>
            <div style={m.field}><span style={m.label}>Start Date</span><span style={m.value}>{data.subscription_start_date || "—"}</span></div>
            <div style={m.field}><span style={m.label}>Subscription End Date</span><span style={m.value}>{data.subscription_validity_end_date || "—"}</span></div>
            <div style={m.field}><span style={m.label}>Frequency</span><span style={m.value}>{data.subscription_frequency || "—"}</span></div>
            <div style={m.field}><span style={m.label}>Next Payment</span><span style={m.value}>{data.next_payment_date || "—"}</span></div>
            <div style={m.field}><span style={m.label}>Last Payment Status</span><span style={m.value}>{data.last_payment_status || "—"}</span></div>
            <div style={m.field}><span style={m.label}>Successful Payments</span><span style={m.value}>{data.number_of_successful_payments ?? "—"}</span></div>
            <div style={m.field}><span style={m.label}>Payment Amount</span><span style={m.value}>{data.payment_amount ? fmtAmount(data.payment_amount) : "—"}</span></div>
            <div style={m.field}><span style={m.label}>Trial Amount</span><span style={m.value}>{data.trail_amount ? fmtAmount(data.trail_amount) : "—"}</span></div>
            <div style={m.field}><span style={m.label}>Trial Status</span><span style={m.value}>{data.trail_status || "—"}</span></div>
          </div>
        )}
      </div>
    </div>
  );
}

function RevokeModal({ appId, onClose, onRevoked }) {
  const [subId, setSubId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const submit = async () => {
    const id = subId.trim();
    if (!id || busy) return;
    setBusy(true);
    setError(null);
    try {
      const sub = await revokeSubscription(appId, id);
      setResult(sub);
      onRevoked(sub);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={m.overlay} onClick={onClose}>
      <div style={{ ...m.box, maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
        <div style={m.header}>
          <h2 style={m.title}>Revoke a Subscription</h2>
          <button onClick={onClose} style={m.closeBtn}>✕</button>
        </div>

        {result ? (
          <div>
            <div style={r.success}>Mandate cancelled at PhonePe.</div>
            <div style={m.field}>
              <span style={m.label}>Subscription ID</span>
              <code style={m.value}>{result.id}</code>
            </div>
            <div style={{ ...m.field, marginTop: 14 }}>
              <span style={m.label}>New State</span>
              <span style={{ alignSelf: "flex-start" }}><Badge state={result.state} /></span>
            </div>
            <div style={r.actions}>
              <button onClick={onClose} style={r.doneBtn}>Done</button>
            </div>
          </div>
        ) : (
          <div>
            <p style={r.hint}>
              Cancels the mandate at PhonePe so no further payments are collected. This cannot be undone.
            </p>
            <input
              value={subId}
              onChange={(e) => setSubId(e.target.value)}
              placeholder="Subscription ID"
              autoFocus
              style={r.input}
              onKeyDown={(e) => e.key === "Enter" && submit()}
            />
            {error && <div style={r.error}>{error}</div>}
            <div style={r.actions}>
              <button onClick={onClose} style={r.cancelBtn}>Cancel</button>
              <button
                onClick={submit}
                disabled={busy || !subId.trim()}
                style={{ ...r.submitBtn, opacity: busy || !subId.trim() ? 0.5 : 1, cursor: busy || !subId.trim() ? "not-allowed" : "pointer" }}
              >
                {busy ? "Revoking…" : "Revoke Subscription"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AppSubscriptions() {
  const { app } = useOutletContext();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stateFilter, setStateFilter] = useState("");
  const [stateCounts, setStateCounts] = useState({});
  const [phoneFilter, setPhoneFilter] = useState("");
  const [subscriptionIdFilter, setSubscriptionIdFilter] = useState("");
  const [phoneInput, setPhoneInput] = useState("");
  const [range, setRange] = useState(() => presetRange(0)); // default: today
  const { start: startDate, end: endDate } = range;
  const [selected, setSelected] = useState(null);
  const [phoneStatus, setPhoneStatus] = useState(null);
  const [showRevoke, setShowRevoke] = useState(false);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [exporting, setExporting] = useState(false);

  // Reflect the new state in the table without a full reload.
  const onRevoked = useCallback((sub) => {
    setRows((prev) => prev.map((row) => (row.id === sub.id ? { ...row, ...sub } : row)));
  }, []);

  const filterParams = useMemo(() => ({
    state: stateFilter || null,
    phone_number: phoneFilter || null,
    subscription_id: subscriptionIdFilter || null,
    start_date: startDate || null,
    end_date: endDate || null,
  }), [stateFilter, phoneFilter, subscriptionIdFilter, startDate, endDate]);

  // Deliberately excludes `state`: the pill counts describe the whole filtered set,
  // so selecting one pill must not zero out the others.
  const countParams = useMemo(() => ({
    phone_number: phoneFilter || null,
    subscription_id: subscriptionIdFilter || null,
    start_date: startDate || null,
    end_date: endDate || null,
  }), [phoneFilter, subscriptionIdFilter, startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    getSubscriptionStateCounts(app.id, countParams)
      .then((c) => { if (!cancelled) setStateCounts(c); })
      .catch(() => { if (!cancelled) setStateCounts({}); });
    return () => { cancelled = true; };
  }, [app.id, countParams]);

  // Export can no longer read from `rows` — that now holds a single page. It
  // pulls the full filtered set on demand instead, so the cost is paid only when
  // someone actually clicks Export rather than on every page load.
  const exportCsv = useCallback(async () => {
    const escape = (v) => {
      if (v == null) return "";
      const s = String(v);
      return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = ["Subscription ID", "Phone", "Trial Amount", "Trial Days", "Max Amount", "Frequency", "State", "Next Payment", "Last Payment Status", "Total Paid", "Created"];
    setExporting(true);
    let all = [];
    try {
      const BATCH = 1000;
      let batch, offset = 0;
      do {
        // Export walks every batch; none of them need the count.
        const res = await listSubscriptionsPage(app.id, { ...filterParams, limit: BATCH, offset, with_count: false });
        batch = res.rows;
        all = all.concat(batch);
        offset += BATCH;
      } while (batch.length === BATCH);
    } finally {
      setExporting(false);
    }

    const csvRows = [
      headers.join(","),
      ...all.map((r) => [
        r.id,
        r.phone_number,
        r.trail_days > 0 ? (r.amount / 100).toFixed(2) : "",
        r.trail_days || 0,
        r.max_amount ? (r.max_amount / 100).toFixed(2) : "",
        r.frequency,
        r.state,
        r.next_payment_date ? new Date(r.next_payment_date).toLocaleDateString("en-IN") : "",
        r.last_payment_status || "",
        r.total_payments || 0,
        fmtDateTime(r.created_at),
      ].map(escape).join(",")),
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `subscriptions_${app.id}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [app.id, filterParams]);

  const applySearch = useCallback(() => {
    const v = phoneInput.trim();
    if (!v) { setPhoneFilter(""); setSubscriptionIdFilter(""); return; }
    if (/^\d{10}$/.test(v)) { setPhoneFilter(v); setSubscriptionIdFilter(""); }
    else { setSubscriptionIdFilter(v); setPhoneFilter(""); }
  }, [phoneInput]);

  // A filter change invalidates the current page number, so drop back to the
  // first page. Kept out of load() so that load can depend on `page` without
  // re-triggering itself.
  useEffect(() => { setPage(0); }, [filterParams]);

  // Fetch only the page being displayed. This used to walk every page of the
  // result set before rendering anything — ~175 sequential requests and ~84 MB
  // of JSON on a large app.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      // The count only needs recomputing when the filtered set changes, and a
      // filter change always resets to page 0 — so ask for it there and reuse it
      // while paging. Saves the count(*) on every Prev/Next.
      const { rows: pageRows, total: count } = await listSubscriptionsPage(app.id, {
        ...filterParams,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        with_count: page === 0,
      });
      setRows(pageRows);
      if (count !== null) {
        setTotal(count);
      } else if (page === 0) {
        // Header missing on a page-0 load: degrade to the page length so the
        // table still renders (the pager just stays hidden).
        setTotal(pageRows.length);
      }
    } finally {
      setLoading(false);
    }
  }, [app.id, filterParams, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <h1 style={s.h1}>Subscriptions</h1>

      {/* State pills */}
      <div style={s.statePills}>
        {STATES.map((st) => {
          const style = STATE_STYLE[st] || { bg: "#f1f5f9", color: "#475569" };
          const active = stateFilter === st;
          const count = st === ""
            ? Object.values(stateCounts).reduce((a, b) => a + b, 0)
            : stateCounts[st];
          return (
            <button
              key={st}
              onClick={() => setStateFilter(st)}
              style={{
                ...s.pill,
                backgroundColor: active ? style.bg : "#f1f5f9",
                color: active ? style.color : "#64748b",
                border: active ? `1.5px solid ${style.color}` : "1.5px solid transparent",
                fontWeight: active ? 700 : 500,
              }}
            >
              {st || "All"}
              <span style={{ ...s.pillCount, color: active ? style.color : "#94a3b8" }}>
                {(count || 0).toLocaleString("en-IN")}
              </span>
            </button>
          );
        })}
      </div>

      {/* Date range — own row so the quick-select pills stay prominent */}
      <div style={s.dateFilters}>
        <DateRangeFilter value={range} onChange={setRange} showWindowLabel />
      </div>

      {/* Filters */}
      <div style={s.filters}>
        <div style={s.phoneSearch}>
          <input
            value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value)}
            placeholder="Phone or Subscription ID"
            style={{ ...s.searchInput, width: 210 }}
            onKeyDown={(e) => e.key === "Enter" && applySearch()}
          />
          <button onClick={applySearch} style={s.searchBtn}>Search</button>
          {(phoneFilter || subscriptionIdFilter) && (
            <button onClick={() => { setPhoneFilter(""); setSubscriptionIdFilter(""); setPhoneInput(""); }} style={s.clearBtn}>✕</button>
          )}
        </div>
        <span style={s.count}>{loading ? "…" : `${total.toLocaleString("en-IN")} results`}</span>
        {rows.length > 0 && (
          <button onClick={exportCsv} disabled={exporting} style={{ ...s.exportBtn, opacity: exporting ? 0.5 : 1, cursor: exporting ? "default" : "pointer" }}>{exporting ? "Exporting…" : "Export CSV"}</button>
        )}
        <button onClick={() => setShowRevoke(true)} style={s.revokeBtn}>Revoke a Subscription</button>
      </div>

      {/* Table */}
      {loading ? (
        <div style={s.loading}>Loading...</div>
      ) : rows.length === 0 ? (
        <div style={s.empty}>No subscriptions found.</div>
      ) : (
        <div style={t.wrapper}>
          <table style={t.table}>
            <thead>
              <tr>
                {["Subscription ID", "Phone", "Trial Amt", "Max Amt", "Frequency", "State", "Next Payment", "Total Paid", "Created"].map((h) => (
                  <th key={h} style={t.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.id} style={i % 2 === 0 ? t.rowEven : t.rowOdd}>
                  <td style={t.td}>
                    <button onClick={() => setSelected(row)} style={t.idBtn}>{row.id}</button>
                  </td>
                  <td style={t.td}>
                    <button onClick={() => setPhoneStatus(row.phone_number)} style={t.idBtn}>{row.phone_number}</button>
                  </td>
                  <td style={t.td}>{row.trail_days > 0 ? `${fmtAmount(row.amount)} (${row.trail_days}d)` : "—"}</td>
                  <td style={t.td}>{row.max_amount ? fmtAmount(row.max_amount) : "—"}</td>
                  <td style={t.td}>{row.frequency}</td>
                  <td style={t.td}><Badge state={row.state} /></td>
                  <td style={t.td}>{fmt(row.next_payment_date)}</td>
                  <td style={t.td}>{row.total_payments}</td>
                  <td style={t.td}>{fmtDateTime(row.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && total > PAGE_SIZE && (
        <div style={s.pagination}>
          <button onClick={() => setPage((p) => p - 1)} disabled={page === 0} style={{ ...s.pageBtn, opacity: page === 0 ? 0.4 : 1 }}>← Prev</button>
          <span style={s.pageInfo}>Page {page + 1} of {Math.ceil(total / PAGE_SIZE)} · {total.toLocaleString("en-IN")} total</span>
          <button onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * PAGE_SIZE >= total} style={{ ...s.pageBtn, opacity: (page + 1) * PAGE_SIZE >= total ? 0.4 : 1 }}>Next →</button>
        </div>
      )}

      {selected && (
        <SubscriptionDetailsModal appId={app.id} subscription={selected} onClose={() => setSelected(null)} />
      )}
      {phoneStatus && (
        <PhoneStatusModal appId={app.id} apiKey={app.api_key} phoneNumber={phoneStatus} onClose={() => setPhoneStatus(null)} />
      )}
      {showRevoke && (
        <RevokeModal appId={app.id} onClose={() => setShowRevoke(false)} onRevoked={onRevoked} />
      )}
    </div>
  );
}

const s = {
  h1: { margin: "0 0 20px", fontSize: 22, color: "#1e293b" },
  statePills: { display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 },
  pill: { padding: "5px 12px", borderRadius: 20, fontSize: 12, cursor: "pointer", background: "none", transition: "all 0.15s" },
  pillCount: { marginLeft: 6, fontSize: 11, fontWeight: 700, opacity: 0.75 },
  dateFilters: { display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" },
  filters: { display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" },
  phoneSearch: { display: "flex", gap: 6 },
  searchInput: { padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, width: 180 },
  searchBtn: { padding: "8px 14px", backgroundColor: "#4f46e5", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13 },
  clearBtn: { padding: "8px 10px", backgroundColor: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 8, cursor: "pointer", fontSize: 13, color: "#64748b" },
  count: { marginLeft: "auto", fontSize: 13, color: "#94a3b8" },
  exportBtn: { padding: "8px 14px", backgroundColor: "#0f766e", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13 },
  revokeBtn: { padding: "8px 14px", backgroundColor: "#fff", color: "#b91c1c", border: "1px solid #fecaca", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 },
  loading: { color: "#64748b", padding: 20 },
  empty: { textAlign: "center", padding: "60px 0", color: "#64748b", fontSize: 14 },
  pagination: { display: "flex", alignItems: "center", gap: 12, justifyContent: "center", marginTop: 16 },
  pageBtn: { padding: "7px 16px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", cursor: "pointer", fontSize: 13, color: "#1e293b" },
  pageInfo: { fontSize: 13, color: "#64748b" },
};

const t = {
  wrapper: { overflowX: "auto", borderRadius: 10, boxShadow: "0 1px 3px rgba(0,0,0,0.07)" },
  table: { width: "100%", borderCollapse: "collapse", backgroundColor: "#fff", fontSize: 13 },
  th: { padding: "10px 14px", textAlign: "left", backgroundColor: "#f8fafc", color: "#64748b", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" },
  td: { padding: "11px 14px", color: "#1e293b", borderBottom: "1px solid #f8fafc", verticalAlign: "middle" },
  rowEven: {},
  rowOdd: { backgroundColor: "#fafafa" },
  badge: { display: "inline-block", padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600 },
  idBtn: { background: "none", border: "none", padding: 0, color: "#4f46e5", cursor: "pointer", fontSize: 13, fontFamily: "monospace", textDecoration: "underline" },
};

const m = {
  overlay: { position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 },
  box: { backgroundColor: "#fff", borderRadius: 12, padding: 28, width: "100%", maxWidth: 720, maxHeight: "90vh", overflowY: "auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  title: { margin: 0, fontSize: 18, color: "#1e293b" },
  closeBtn: { background: "none", border: "none", fontSize: 16, cursor: "pointer", color: "#64748b" },
  grid: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 14, marginBottom: 24 },
  field: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em" },
  value: { fontSize: 13, color: "#1e293b", wordBreak: "break-all" },
};

const r = {
  hint: { margin: "0 0 16px", fontSize: 13, color: "#64748b", lineHeight: 1.5 },
  input: { width: "100%", boxSizing: "border-box", padding: "10px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontFamily: "monospace" },
  error: { marginTop: 12, padding: "9px 12px", backgroundColor: "#fee2e2", color: "#991b1b", borderRadius: 8, fontSize: 12.5, lineHeight: 1.45 },
  success: { marginBottom: 18, padding: "9px 12px", backgroundColor: "#dcfce7", color: "#166534", borderRadius: 8, fontSize: 13, fontWeight: 600 },
  actions: { display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 },
  cancelBtn: { padding: "8px 16px", backgroundColor: "#fff", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 8, cursor: "pointer", fontSize: 13 },
  submitBtn: { padding: "8px 16px", backgroundColor: "#dc2626", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600 },
  doneBtn: { padding: "8px 16px", backgroundColor: "#4f46e5", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 },
};
